/**
 * license-waste-brief.ts — Editorial copy layer for the License Waste headline.
 *
 * Composes the verdict-and-deck for the License Waste card from the active
 * licensing posture (total monthly idle-seat spend + a top-3 list of idle
 * SKUs). The voice is "trusted advisor": short sentences, no em-dashes,
 * period-separated rather than dash-bridged. The verdict is capped at six
 * words so the score-box quote always fits.
 *
 * Each editorial slot has a small set of variants. A variant is picked
 * deterministically from a `${tenantId}:${localDay}:${slot}` seed so the same
 * tenant sees a stable brief through the day, and different days vary.
 *
 * Voice anchor: "Chaos to clarity / strategically human." Lead with named
 * subjects (the noisy SKU, the unassigned-seat count) — never with a row
 * count or a metric grid label. When data is missing, surface what we have
 * and frame the gap; do not fabricate.
 */

import { localDayKey, pickVariant } from './brief-helpers'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export type LicenseWasteSeverity = 'clean' | 'low' | 'moderate' | 'large' | 'unknown'

export interface LicenseWasteIdleSku {
  /** Friendly SKU name, e.g. "Microsoft 365 E5". */
  displayName: string
  /** Number of paid-but-unassigned seats on this SKU. */
  idleSeats: number
  /** Estimated monthly $ waste for this SKU at list price. */
  estimatedMonthlyWasteUsd: number
}

export interface LicenseWasteBriefInput {
  /** Total monthly idle-seat cost in USD. null when catalog has no matches. */
  totalMonthlyWasteUsd?: number | null
  /** Annual figure for emphasis in the lead sentence. Optional — derive *12 if missing. */
  totalAnnualWasteUsd?: number | null
  /** Top idle SKUs by waste $ (server returns up to 3, descending). */
  topIdleSkus?: Array<LicenseWasteIdleSku>
  /** Total idle seat count across all SKUs (for the "N seats sit idle" line). */
  totalIdleSeats?: number
  /** Used to seed deterministic variant picks (per tenant + day stability). */
  tenantId?: string
  /** Override "today" for tests. */
  asOfDate?: Date
}

export interface LicenseWasteBrief {
  /** ≤6-word italic-quotes verdict. */
  verdict: string
  /** "$1,240/mo" headline number — formatted USD, falls back to "—" when unknown. */
  headlineMonthly: string
  /** Plain-text byline: "By Guardian. As of {time}." */
  byline: string
  /** One sentence under the headline. Lead with named subject. */
  leadSentence: string
  /** Severity bucket for color treatment in the consuming component. */
  severity: LicenseWasteSeverity
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

/** Format the "By Guardian. As of HH:mm GMT. Pricing at Microsoft retail list." byline.
 *  en-GB + short time. The retail-list scope tells the reader the dollar figures
 *  are estimates against published list pricing, not their negotiated rate. */
function formatByline(now: Date): string {
  const fmt = new Intl.DateTimeFormat('en-GB', { timeStyle: 'short' })
  return `By Guardian. As of ${fmt.format(now)} GMT. Pricing at Microsoft retail list.`
}

/** Severity bucket — drives both the verdict table and the color treatment. */
function bucketSeverity(monthly: number | null | undefined): LicenseWasteSeverity {
  if (monthly == null) return 'unknown'
  if (monthly === 0) return 'clean'
  if (monthly < 100) return 'low'
  if (monthly < 1000) return 'moderate'
  return 'large'
}

/**
 * Format a USD/month figure for the headline.
 *   - null/undefined → em-dash (visually matches "no data" cells)
 *   - sub-$1 → "$0.50/mo" (two-decimal so the value isn't lost in rounding)
 *   - $1 to $10 → "$3/mo" (whole dollars are cleaner; rounding loss is small)
 *   - >$10 → "$1,240/mo" (whole dollars, thousands-grouped)
 */
function formatUsdMonthly(amount: number | null | undefined): string {
  if (amount == null) return '—' // em-dash
  if (amount > 0 && amount < 1) {
    return `$${amount.toFixed(2)}/mo`
  }
  const rounded = Math.round(amount)
  return `$${rounded.toLocaleString('en-US')}/mo`
}

/** Annual call-out, e.g. "$14,880/yr at list price". */
function formatUsdAnnual(amount: number): string {
  const rounded = Math.round(amount)
  return `$${rounded.toLocaleString('en-US')}/yr at list price`
}

/** Format an idle-SKU's monthly waste for inline lead-sentence use:
 *  "≈$540/mo". Sub-$1 collapses to "≈$1/mo" so the lead sentence reads
 *  cleanly when a SKU happens to have trivial price data. */
function formatSkuMonthly(amount: number): string {
  const rounded = Math.max(1, Math.round(amount))
  return `≈$${rounded.toLocaleString('en-US')}/mo`
}

/* -------------------------------------------------------------------------- */
/* Verdict variants (six-words-or-fewer per the score-box cap)                */
/* -------------------------------------------------------------------------- */

const VERDICT_CLEAN: readonly string[] = [
  'Spend looks tight. Nothing idle.',
  'All seats earning their keep.',
  'Clean books on licensing.',
]

const VERDICT_LOW: readonly string[] = [
  'A small leak. Easy fix.',
  'Minor idle spend. Worth a look.',
  'One small drip to plug.',
]

const VERDICT_MODERATE: readonly string[] = [
  'Real money sitting idle.',
  'Some seats riding the bench.',
  'A meaningful leak. Worth closing.',
]

const VERDICT_LARGE: readonly string[] = [
  'Big leak. Plug the biggest first.',
  'A meaningful leak. Worth chasing.',
  'Significant waste. Start at the top.',
]

const VERDICT_UNKNOWN: readonly string[] = [
  'Pricing data still loading.',
  'Reading licensing posture.',
  'Catalog warming up.',
]

const VERDICT_TABLES: Record<LicenseWasteSeverity, readonly string[]> = {
  clean: VERDICT_CLEAN,
  low: VERDICT_LOW,
  moderate: VERDICT_MODERATE,
  large: VERDICT_LARGE,
  unknown: VERDICT_UNKNOWN,
}

/* -------------------------------------------------------------------------- */
/* Lead-sentence variants (one short sentence under the headline)             */
/* -------------------------------------------------------------------------- */

/**
 * Templates take optional tokens:
 *   {sku}         — top-idle-SKU display name
 *   {idleSeats}   — top-idle-SKU idle-seat count (already formatted)
 *   {idleWaste}   — top-idle-SKU monthly $ (already formatted, "≈$540/mo")
 *   {totalIdle}   — total-idle-seat count (already formatted)
 *   {annual}      — annualized waste figure (already formatted)
 *
 * Each template carries the substitution tokens it needs; the picker swaps
 * them out before returning. We keep the substitution in one place so the
 * variants below stay readable as natural sentences.
 */

/** Moderate or large severity, with a top-idle-SKU to name. */
const LEAD_HAS_TOP_SKU_MODERATE: readonly string[] = [
  '{sku} accounts for most of the unused spend ({idleSeats} idle seats, {idleWaste}).',
  '{sku} carries most of the waste right now ({idleSeats} idle seats, {idleWaste}).',
  'The waste concentrates on {sku} ({idleSeats} idle seats, {idleWaste}).',
]

const LEAD_HAS_TOP_SKU_LARGE: readonly string[] = [
  '{sku} is the biggest drain at {idleWaste}. {annual}.',
  '{sku} leads the idle list at {idleWaste}. That is {annual}.',
  'Most of the spend leaks through {sku} at {idleWaste}. {annual}.',
]

/** Moderate / large with no SKU data — generic "named-count" fallback. */
const LEAD_NO_TOP_SKU_MODERATE: readonly string[] = [
  '{totalIdle} unassigned seats are quietly billing each month.',
  'Roughly {totalIdle} paid seats are sitting unused on the license list.',
  '{totalIdle} seats are paid for but unclaimed. Worth a sweep.',
]

const LEAD_NO_TOP_SKU_LARGE: readonly string[] = [
  '{totalIdle} unassigned seats are quietly billing each month. {annual}.',
  '{totalIdle} paid seats sit idle on the list. Annualized, that is {annual}.',
  'A large stack of paid-but-unused seats ({totalIdle}). {annual}.',
]

/** Low severity. Smaller stakes, lighter language. */
const LEAD_LOW_HAS_TOTAL: readonly string[] = [
  '{totalIdle} seats sit unassigned. Tidy them up at your next license review.',
  '{totalIdle} unused seats on the list. Easy housekeeping.',
  'A few unused seats ({totalIdle}). Worth a quick tidy at next review.',
]

const LEAD_LOW_NO_TOTAL: readonly string[] = [
  'A small idle balance is sitting on the license list. Routine cleanup.',
  'A small drip on the license list. Tidy at next review.',
  'A modest idle balance. Easy housekeeping.',
]

/** Clean severity. */
const LEAD_CLEAN_WITH_SKUS: readonly string[] = [
  'Every paid seat is in active use across {n} SKUs.',
  'Across {n} SKUs, every paid seat is assigned and earning.',
  '{n} SKUs in use, no idle balance worth chasing.',
]

const LEAD_CLEAN_NO_SKUS: readonly string[] = [
  'Every paid seat is in active use right now.',
  'No idle balance worth chasing. Spend lines up with usage.',
  'Spend tracks usage cleanly. Nothing idle to reclaim.',
]

/** Unknown severity (loading / no catalog match). */
const LEAD_UNKNOWN: readonly string[] = [
  'Pricing catalog initializing. Your dollar figure shows up after the next refresh.',
  'Licensing posture is reading in. Numbers settle once the catalog warms.',
  'Catalog warming up. The dollar figure resolves on the next refresh.',
]

/* -------------------------------------------------------------------------- */
/* Lead-sentence builder                                                      */
/* -------------------------------------------------------------------------- */

function buildLeadSentence(
  severity: LicenseWasteSeverity,
  input: LicenseWasteBriefInput,
  seed: string,
): string {
  const top = input.topIdleSkus?.[0]
  const totalIdle =
    typeof input.totalIdleSeats === 'number' && input.totalIdleSeats > 0
      ? input.totalIdleSeats
      : undefined

  if (severity === 'unknown') {
    return pickVariant(LEAD_UNKNOWN, seed, 'lead.unknown')
  }

  if (severity === 'clean') {
    const skuCount = input.topIdleSkus?.length ?? 0
    if (skuCount > 0) {
      const tpl = pickVariant(LEAD_CLEAN_WITH_SKUS, seed, 'lead.clean.skus')
      return tpl.replace('{n}', String(skuCount))
    }
    return pickVariant(LEAD_CLEAN_NO_SKUS, seed, 'lead.clean.noSkus')
  }

  if (severity === 'low') {
    if (totalIdle != null) {
      const tpl = pickVariant(LEAD_LOW_HAS_TOTAL, seed, 'lead.low.total')
      return tpl.replace('{totalIdle}', String(totalIdle))
    }
    return pickVariant(LEAD_LOW_NO_TOTAL, seed, 'lead.low.noTotal')
  }

  // moderate or large from here on.
  // Resolve the annual figure: prefer the explicit value, otherwise derive
  // *12 from the monthly. This is the only place we need it — the public
  // brief shape doesn't expose annual on its own.
  const monthly =
    typeof input.totalMonthlyWasteUsd === 'number' ? input.totalMonthlyWasteUsd : 0
  const annualValue =
    typeof input.totalAnnualWasteUsd === 'number'
      ? input.totalAnnualWasteUsd
      : monthly * 12
  const annualPhrase = formatUsdAnnual(annualValue)

  if (top) {
    const table = severity === 'large' ? LEAD_HAS_TOP_SKU_LARGE : LEAD_HAS_TOP_SKU_MODERATE
    const slot = severity === 'large' ? 'lead.large.sku' : 'lead.moderate.sku'
    const tpl = pickVariant(table, seed, slot)
    return tpl
      .replace('{sku}', top.displayName)
      .replace('{idleSeats}', String(top.idleSeats))
      .replace('{idleWaste}', formatSkuMonthly(top.estimatedMonthlyWasteUsd))
      .replace('{annual}', annualPhrase)
  }

  // No SKU detail — fall back to a count-only sentence (still leads with
  // a named subject: the unassigned-seat count, not the row count).
  const fallbackTable =
    severity === 'large' ? LEAD_NO_TOP_SKU_LARGE : LEAD_NO_TOP_SKU_MODERATE
  const fallbackSlot = severity === 'large' ? 'lead.large.noSku' : 'lead.moderate.noSku'
  const tpl = pickVariant(fallbackTable, seed, fallbackSlot)

  // If we don't even have an idle-seat count, soften "{totalIdle}" to a
  // plain word so the sentence still reads cleanly. (This is a rare path —
  // the server already returns totalIdleSeats whenever it returns waste $.)
  const display = totalIdle != null ? String(totalIdle) : 'Several'
  return tpl.replace('{totalIdle}', display).replace('{annual}', annualPhrase)
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a LicenseWasteBrief from the active licensing posture. All inputs
 * are optional — the function degrades gracefully toward a plausible "no
 * data" brief rather than throwing.
 *
 * When `totalMonthlyWasteUsd` is null/undefined we surface the catalog-warming
 * variants and an em-dash headline rather than coercing to $0/mo, which would
 * land us in the "clean" band and tell the user something we don't actually
 * know.
 *
 * Annual figure: caller can pass it explicitly; otherwise we derive *12 from
 * the monthly figure. We never fabricate it from the SKU list.
 */
export function composeLicenseWasteBrief(
  input: LicenseWasteBriefInput = {},
): LicenseWasteBrief {
  const now = input.asOfDate ?? new Date()
  const severity = bucketSeverity(input.totalMonthlyWasteUsd)

  // Daily seed: stable per (tenant, local day). Tenants share a seed when
  // unset (still deterministic for tests + anonymous flows).
  const seed = `${input.tenantId ?? 'guardian'}:${localDayKey(now)}`

  const verdict = pickVariant(VERDICT_TABLES[severity], seed, `verdict.${severity}`)
  const headlineMonthly = formatUsdMonthly(input.totalMonthlyWasteUsd)
  const byline = formatByline(now)
  const leadSentence = buildLeadSentence(severity, input, seed)

  return {
    verdict,
    headlineMonthly,
    byline,
    leadSentence,
    severity,
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Variant tables exposed for shape-assertions in tests. Not part of the
 *  public runtime API; do not import from app code. */
export const __TEST_ONLY__ = {
  VERDICT_CLEAN,
  VERDICT_LOW,
  VERDICT_MODERATE,
  VERDICT_LARGE,
  VERDICT_UNKNOWN,
  VERDICT_TABLES,
  LEAD_HAS_TOP_SKU_MODERATE,
  LEAD_HAS_TOP_SKU_LARGE,
  LEAD_NO_TOP_SKU_MODERATE,
  LEAD_NO_TOP_SKU_LARGE,
  LEAD_LOW_HAS_TOTAL,
  LEAD_LOW_NO_TOTAL,
  LEAD_CLEAN_WITH_SKUS,
  LEAD_CLEAN_NO_SKUS,
  LEAD_UNKNOWN,
  formatUsdMonthly,
  formatUsdAnnual,
  formatSkuMonthly,
  bucketSeverity,
}
